import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Search } from "lucide-react";

/**
 * SubjectAutocomplete
 *
 * Multi-select chip picker. When `suggestEndpoint` is set (default
 * `/subjects/suggest`) it fetches server-side canonical suggestions —
 * useful for the "Intended degrees" field where synonym-grouping matters.
 * Set `suggestEndpoint={null}` to disable the dropdown entirely and get a
 * pure free-text chip input (used for Dream Employers where any brand
 * name is valid and suggesting subjects is misleading).
 *
 * Props:
 *   values: string[]                  — currently selected chips
 *   onChange: (string[]) => void      — update handler
 *   placeholder?: string
 *   maxChips?: number                 — soft cap (default 5)
 *   suggestEndpoint?: string | null   — API path (default `/subjects/suggest`)
 *   testIdPrefix?: string             — for data-testids
 */
export function SubjectAutocomplete({
  values = [],
  onChange,
  placeholder = "e.g. Politics, Economics, Computer Science…",
  maxChips = 5,
  suggestEndpoint = "/subjects/suggest",
  testIdPrefix = "subject-picker",
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const canAddMore = values.length < maxChips;

  // Debounced fetch — skipped entirely when no suggestion endpoint is
  // configured (Dream Employers case: free-text only).
  useEffect(() => {
    if (!open || !suggestEndpoint) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(suggestEndpoint, {
          params: { q: query.trim(), limit: 8 },
        });
        // Filter out already-picked canonicals
        const pickedCanonicals = new Set(
          values.map((v) => (v || "").trim().toLowerCase()),
        );
        setSuggestions(
          (data || []).filter(
            (s) =>
              !pickedCanonicals.has((s.label || "").toLowerCase())
              && !pickedCanonicals.has((s.canonical || "").toLowerCase()),
          ),
        );
      } catch {
        setSuggestions([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [query, open, values]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const addChip = (label) => {
    const l = (label || "").trim();
    if (!l || !canAddMore) return;
    if (values.some((v) => v.toLowerCase() === l.toLowerCase())) return;
    onChange([...values, l]);
    setQuery("");
    setActiveIdx(0);
    inputRef.current?.focus();
  };

  const removeChip = (label) => {
    onChange(values.filter((v) => v !== label));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions[activeIdx]) {
        addChip(suggestions[activeIdx].label);
      } else if (query.trim()) {
        // Free-text add — helps for niche subjects not yet in DB.
        addChip(query.trim());
      }
    } else if (e.key === "Backspace" && !query && values.length) {
      removeChip(values[values.length - 1]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "," || e.key === "Tab") {
      // Comma or Tab commits current query as a chip.
      if (query.trim()) {
        e.preventDefault();
        addChip(query.trim());
      }
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testIdPrefix}>
      {/* Chip row + input */}
      <div
        className="flex flex-wrap gap-1.5 border border-stone-300 rounded-md bg-white px-2 py-1.5 min-h-[40px] focus-within:border-[#4C1D95] focus-within:ring-2 focus-within:ring-[#4C1D95]/20 transition-colors"
        onClick={() => inputRef.current?.focus()}>
        {values.map((v) => (
          <span
            key={v}
            data-testid={`${testIdPrefix}-chip-${v.toLowerCase().replace(/\s+/g, "-")}`}
            className="inline-flex items-center gap-1 bg-[#4C1D95]/10 text-[#4C1D95] border border-[#4C1D95]/30 rounded-full pl-2.5 pr-1 py-0.5 text-sm">
            <span>{v}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeChip(v); }}
              aria-label={`Remove ${v}`}
              data-testid={`${testIdPrefix}-remove-${v.toLowerCase().replace(/\s+/g, "-")}`}
              className="ml-0.5 rounded-full p-0.5 hover:bg-[#4C1D95]/20">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          data-testid={`${testIdPrefix}-input`}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm px-1 py-0.5"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            // Auto-commit whatever the user typed as a free-text chip when
            // they click away without pressing Enter/comma/Tab. Prevents
            // the "I typed Economics but it never got saved" bug where
            // the raw query silently gets dropped on submit.
            const q = query.trim();
            if (q) addChip(q);
          }}
          placeholder={values.length === 0 ? placeholder : ""}
          disabled={!canAddMore}
        />
      </div>

      {/* Helper text */}
      <div className="text-xs text-stone-500 mt-1 font-mono">
        {values.length}/{maxChips} · press Enter or comma to add
        {suggestEndpoint ? " · synonyms auto-matched" : ""}
      </div>

      {/* Dropdown */}
      {open && suggestEndpoint && suggestions.length > 0 && canAddMore && (
        <div
          className="absolute left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}>
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={s.canonical}
              data-testid={`${testIdPrefix}-option-${s.canonical.replace(/\s+/g, "-")}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addChip(s.label)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 text-sm border-b border-stone-100 last:border-b-0 transition-colors ${
                i === activeIdx ? "bg-[#F3F1EC]" : "bg-white hover:bg-stone-50"
              }`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.label}</div>
                  {s.aliases && s.aliases.length > 0 && (
                    <div className="text-[11px] text-stone-500 font-mono truncate">
                      also: {s.aliases.slice(0, 4).join(", ")}
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-stone-500 font-mono shrink-0">
                  {s.count > 0 ? `${s.count} degree${s.count === 1 ? "" : "s"}` : "add"}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SubjectAutocomplete;
